import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export function Reveal({ children, delay = 0, y = 32, className = "", once = true, as = "div", ...rest }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "0px 0px -12% 0px" });
  const reduced = useReducedMotion();
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      ref={ref}
      className={className}
      initial={reduced ? false : { opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 1.1, ease: EASE, delay }}
      {...rest}
    >
      {children}
    </Comp>
  );
}

export function MaskedLines({ lines, className = "", lineClassName = "", delay = 0, stagger = 0.12, inView: forceInView, as = "h1", ...rest }) {
  const ref = useRef(null);
  const auto = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
  const show = forceInView ?? auto;
  const reduced = useReducedMotion();
  const Comp = as;
  return (
    <Comp ref={ref} className={className} {...rest}>
      {lines.map((line, i) => (
        <span key={i} className="block overflow-hidden" style={{ paddingBottom: "0.08em", marginBottom: "-0.08em" }}>
          <motion.span
            className={`block ${lineClassName}`}
            initial={reduced ? false : { y: "110%" }}
            animate={show ? { y: 0 } : undefined}
            transition={{ duration: 1.2, ease: EASE, delay: delay + i * stagger }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Comp>
  );
}

export function ImageReveal({ src, alt, className = "", imgClassName = "", delay = 0, sizes, priority = false }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
  const reduced = useReducedMotion();
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.div
        className="absolute inset-0 bg-stone z-10"
        initial={reduced ? { scaleY: 0 } : { scaleY: 1 }}
        animate={inView ? { scaleY: 0 } : undefined}
        style={{ transformOrigin: "top" }}
        transition={{ duration: 1.2, ease: EASE, delay }}
      />
      <motion.img
        src={src}
        alt={alt}
        sizes={sizes}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        className={`w-full h-full object-cover ${imgClassName}`}
        initial={reduced ? false : { scale: 1.15 }}
        animate={inView ? { scale: 1 } : undefined}
        transition={{ duration: 1.8, ease: EASE, delay }}
      />
    </div>
  );
}

export { EASE };
